/**
 * VacationRequestCard - Compact card display for vacation requests
 * Used in mobile/list views - Story: VAC-DS-03
 */

import React from 'react'
import { Eye, X } from 'lucide-react'
import type { VacationRequest } from '@/types/vacation'
import { formatDateRange } from '@/lib/utils/date-utils'

interface VacationRequestCardProps {
  request: VacationRequest
  onView: (request: VacationRequest) => void
  onCancel?: (requestId: string) => void
}

const getStatusClass = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'status-primary'
    case 'PENDING':
      return 'status-warning'
    case 'REJECTED':
      return 'status-error'
    case 'CANCELLED':
      return 'bg-brand-dark-light text-brand-gray'
    default:
      return 'bg-brand-dark-light text-brand-gray'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'Aprovado'
    case 'PENDING':
      return 'Pendente'
    case 'REJECTED':
      return 'Rejeitado'
    case 'CANCELLED':
      return 'Cancelado'
    default:
      return status
  }
}

export const VacationRequestCard: React.FC<VacationRequestCardProps> = ({
  request,
  onView,
  onCancel
}) => {
  const canCancel = request.status === 'PENDING' && onCancel

  return (
    <div
      className="card card-hover cursor-pointer"
      onClick={() => onView(request)}
    >
      {/* Card Header */}
      <div className="flex justify-between items-start mb-4">
        <span className={`px-3 py-1 rounded-monumenta text-xs font-display font-semibold ${getStatusClass(request.status)}`}>
          {getStatusLabel(request.status)}
        </span>
        <span className="text-xs text-brand-gray font-mono">
          {request.requestId}
        </span>
      </div>

      {/* Date Range */}
      <h3 className="text-xl font-display font-semibold text-white mb-3">
        {formatDateRange(request.startDate, request.endDate)}
      </h3>

      {/* Days Info */}
      <div className="mb-3">
        <span className="text-xs text-brand-gray font-body">Dias</span>
        <p className="text-base text-white font-display font-semibold">
          {request.totalDays}
        </p>
      </div>

      {/* Notes */}
      {request.notes && (
        <p className="text-sm text-brand-gray truncate mb-4 font-body">
          {request.notes}
        </p>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t border-brand-dark-light">
        <button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onView(request)
          }}
          className="btn-icon btn-ghost"
          aria-label="visualizar"
        >
          <Eye size={16} />
        </button>

        {canCancel && (
          <button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onCancel(request.requestId)
            }}
            className="btn-icon text-error hover:bg-error/10"
            aria-label="cancelar"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

export const VacationRequestCardSkeleton: React.FC = () => {
  return (
    <div className="card animate-pulse">
      <div className="flex justify-between items-start mb-4">
        <div className="w-20 h-6 bg-brand-dark-light rounded"></div>
        <div className="w-24 h-4 bg-brand-dark-light rounded"></div>
      </div>
      <div className="w-3/5 h-6 bg-brand-dark-light rounded mb-3"></div>
      <div className="w-10 h-5 bg-brand-dark-light rounded"></div>
    </div>
  )
}

// Re-export VacationRequestList from its own file
export { VacationRequestList } from './VacationRequestList'
